import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Clock,
  Eye,
  Users,
} from "lucide-react";
import { supabase } from "../lib/supabase";
import SEO from "./SEO";
import CoursePreviewModal from "./CoursePreviewModal";

export default function AdminCourses() {
  const [courses, setCourses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewCourse, setPreviewCourse] = useState<string | null>(null);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("courses")
        .select(
          `
          *,
          category:course_categories(*),
          tags:course_tags_map(
            course_tags(*)
          )
        `,
        )
        .eq("deleted", false)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setCourses(data || []);
    } catch (err) {
      console.error("Error fetching courses:", err);
      setError(err instanceof Error ? err.message : "Failed to load courses");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this course?")) return;

    try {
      const { error } = await supabase
        .from("courses")
        .update({ deleted: true })
        .eq("id", id);

      if (error) throw error;
      await fetchCourses();
    } catch (err) {
      console.error("Error deleting course:", err);
      alert("Failed to delete course");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <SEO
        title="One80Labs - Manage Courses"
        description="Admin interface for managing courses in the One80Labs content management system."
        noindex={true}
      />
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Courses</h1>
        <Link
          to="/admin/courses/new"
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
        >
          <Plus className="h-5 w-5 mr-2" />
          New Course
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {courses.map((course) => (
          <div
            key={course.id}
            className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
          >
            {course.image_url && (
              <img
                src={course.image_url}
                alt={course.title}
                className="w-full h-48 object-cover"
              />
            )}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <span
                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                    course.is_published
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {course.is_published ? "Published" : "Draft"}
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPreviewCourse(course.slug)}
                    className="text-gray-600 hover:text-gray-900"
                  >
                    <Eye className="h-5 w-5" />
                  </button>
                  <Link
                    to={`/admin/courses/${course.id}`}
                    className="text-[#F98B3D] hover:text-[#e07a2c]"
                  >
                    <Pencil className="h-5 w-5" />
                  </Link>
                  <button
                    onClick={() => handleDelete(course.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {course.title}
              </h3>

              <div className="flex items-center text-sm text-gray-500 mb-2">
                <Clock className="h-4 w-4 mr-1" />
                {course.duration}
              </div>

              <div className="flex items-center text-sm text-gray-500 mb-2">
                <Users className="h-4 w-4 mr-1" />
                Max {course.max_students} students
              </div>

              {course.published_at && (
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Calendar className="h-4 w-4 mr-1" />
                  {new Date(course.published_at).toLocaleDateString()}
                </div>
              )}

              <div className="mt-4 pt-4 border-t border-gray-100">
                {course.category && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Category:</span>{" "}
                    {course.category.name}
                  </div>
                )}
                {course.tags && course.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {course.tags.map(
                      (tagMap: any) =>
                        tagMap.course_tags && (
                          <span
                            key={tagMap.course_tags.id}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                          >
                            {tagMap.course_tags.name}
                          </span>
                        ),
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <CoursePreviewModal
        isOpen={!!previewCourse}
        onClose={() => setPreviewCourse(null)}
        slug={previewCourse || ""}
      />
    </div>
  );
}
